import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { isEmpty } from 'remeda';

import { qrcodeAPIService } from '~/api/qrcode';
import { NEW_QR_CODES_TEMPLATE_IDS } from '~/constants/common';
import { HTTP_STATUSE_CODES } from '~/constants/http-statuses-constants';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import PasswordProtectedModal from '~/pages/MyMicrosite/PasswordProtectedModal';
import { useToastStore } from '~/store/useToastStore';

import { micrositeTypeComponent } from './utils/MircositeComponents';
import { previewModelData } from './utils/model-data';

export default function MyMicrosite() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setShow } = useToastStore();
  const { id: qrCodeUri = '' } = useParams();
  const [searchParams] = useSearchParams();
  const passwordIsRequired = searchParams.get('password_required') === 'true';
  const [qrCodeData, setQrCodeData] = useState<any>({});

  const loadQrCodeData = async (password?: string) => {
    try {
      const response = await qrcodeAPIService.getQrCodeByURI({ id: qrCodeUri, password });
      const micrositeType = response?.data?.details?.micrositeType;

      if (micrositeType === 'website') {
        window.location.href = response?.data?.details?.microsite?.url;
      } else {
        if (response?.data?.redirectUrl) {
          window.location.replace(response?.data?.redirectUrl);
        } else {
          if (micrositeType) {
            if (!NEW_QR_CODES_TEMPLATE_IDS.includes(micrositeType)) {
              previewModelData[micrositeType](response.data?.details?.microsite, dispatch, response.data?.details);
            }
            setQrCodeData(response.data?.details);
          }
        }
      }
    } catch (e) {
      if (e?.response?.status === HTTP_STATUSE_CODES.NOT_FOUND) {
        navigate(PATH.QR.NOT_FOUND);
      } else if (e.message) {
        if (e.message === 'Invalid password' && !passwordIsRequired) {
          const searchParams = new URLSearchParams(location.search);
          searchParams.set('password_required', 'true');
          navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
        } else {
          setShow({
            message: e.message,
            type: 'error',
            show: true,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!passwordIsRequired) {
      void loadQrCodeData();
    }
  }, []);

  if (passwordIsRequired && isEmpty(qrCodeData)) {
    return <PasswordProtectedModal isOpen={true} onSubmit={loadQrCodeData} />;
  }

  if (qrCodeData?.microsite?.directPdfLink) {
    return (
      <Box sx={{ height: '100vh', width: '100%' }}>
        <iframe
          src={qrCodeData?.microsite?.pdfs[0]?.file}
          height={document.documentElement.scrollHeight}
          width={document.body.clientWidth}
          style={{ border: 'none' }}
        />
      </Box>
    );
  }

  return (
    <Box>
      {qrCodeData?.micrositeType &&
        micrositeTypeComponent?.[qrCodeData?.micrositeType](
          qrCodeData?.microsite?.selectedTemplate,
          qrCodeData?.microsite,
        )}
    </Box>
  );
}
